/**
 * Image utilities
 *
 * @author Erik Jan Lam
 * @date Jan 08 2019
 */

/**
 * Execute a callback when an image is loaded.
 * Fixes issue with browsers not firing the load event when the image is loaded from cache.
 *
 * @date 21-09-2017
 * @author Erik Jan Lam
 */

/**
 * When the image is loaded, execute the callback.
 * @param {HTMLImageElement} image The image node.
 * @param {function} callback The callback.
 * @param {boolean | *} onlyWhenNotLoaded Only execute the callback if the image is not yet loaded.
 */
function onImgLoaded(image, callback, onlyWhenNotLoaded) {
    if (image.nodeType === 1 && image.tagName.toLowerCase() === 'img') {
        if (image.complete && image.naturalWidth) {
            // if the image is already loaded, execute the callback.
            if (!onlyWhenNotLoaded) {
                callback.apply(image);
            }
        } else {
            // attach the load listener.
            image.addEventListener('load', function () {
                callback.apply(image);
            });
        }
    }
}

exports.loaded = onImgLoaded;
exports.allLoaded = function (images, callback) {
    var loaded = 0;

    function imageLoaded() {
        loaded++;
        if (loaded === images.length) {
            callback();
        }
    }

    for (var i = 0; i < images.length; i++) {
        onImgLoaded(images[i], imageLoaded);
    }
};

/**
 * Get the rendered size of an image that is styled with object-fit.
 * @param {HTMLImageElement} img The image.
 * @return {{naturalHeight: number, x: number, y: number, renderedHeight: number, renderedWidth: number, naturalWidth: number}}
 */
exports.getRenderedSize = function (img) {
    var style = window.getComputedStyle(img);
    var fit = style.getPropertyValue('object-fit');
    var position = style.getPropertyValue('object-position').split(' ');
    var size = img.getBoundingClientRect();
    var imageRatio = img.naturalWidth / img.naturalHeight; // > 1 = landscape, < 1 = portrait;
    var containerRatio = size.width / size.height;
    var scaleFactor;

    var data = {
        renderedWidth: size.width,
        renderedHeight: size.height,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        scaleFactor: size.width / img.naturalWidth,
        x: 0,
        y: 0
    };

    if (fit !== 'fill') {
        // fill is default, when no object-fit property is set.
        if (fit === 'cover') {
            if (imageRatio <= containerRatio) {
                // image fills width
                scaleFactor = size.width / img.naturalWidth;
                data.renderedWidth = size.width;
                data.renderedHeight = img.naturalHeight * scaleFactor;
                data.x = 0;
                data.y = ((data.renderedHeight - size.height) * parseInt(position[1])) / 100;
            } else {
                // image fills height
                scaleFactor = size.height / img.naturalHeight;
                data.renderedHeight = size.height;
                data.renderedWidth = img.naturalWidth * scaleFactor;
                data.x = ((data.renderedWidth - size.width) * parseInt(position[0])) / 100;
                data.y = 0;
            }
        } else if (fit === 'contain') {
            if (imageRatio <= containerRatio) {
                // image fills height
                scaleFactor = size.height / img.naturalHeight;
                data.renderedHeight = size.height;
                data.renderedWidth = img.naturalWidth * scaleFactor;
                data.x = ((size.width - data.renderedWidth) * parseInt(position[0])) / 100;
                data.y = 0;
            } else {
                // image fills width
                scaleFactor = size.width / img.naturalWidth;
                data.renderedWidth = size.width;
                data.renderedHeight = img.naturalHeight * scaleFactor;
                data.x = 0;
                data.y = ((size.height - data.renderedHeight) * parseInt(position[1])) / 100;
            }
        }

        data.scaleFactor = scaleFactor;
    }

    return data;
};
